import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/Button";
import { Card, CardHeader } from "@/components/ui/Card";
import { Dialog } from "@/components/ui/Dialog";
import { Alert, Badge } from "@/components/ui/Feedback";
import { formatDateTime, formatDuration, formatNumber, parseApiDate } from "@/lib/format";
import { importsApi, type Job, type JobStatus } from "@/lib/imports-api";

import { ProgressBar } from "./ProgressBar";

const ACTIVE: JobStatus[] = ["queued", "running"];
const TONE: Record<JobStatus, "neutral" | "primary" | "accent" | "danger"> = {
  queued: "neutral",
  running: "primary",
  succeeded: "primary",
  failed: "danger",
  interrupted: "danger",
  cancelled: "neutral",
};

function jobIsActive(job: Job): boolean {
  return ACTIVE.includes(job.status);
}

function elapsed(job: Job): string | null {
  if (!job.started_at) return null;
  const end = job.finished_at ? parseApiDate(job.finished_at) : new Date();
  return formatDuration((end.getTime() - parseApiDate(job.started_at).getTime()) / 1000);
}

function JobProgress({ job }: { job: Job }) {
  const { t } = useTranslation();
  const { stage, done = 0, total = 0 } = job.progress;
  if (job.status !== "running" || !stage) return null;
  const ratio = total ? done / total : 0;
  return (
    <div className="min-w-40 space-y-1">
      <div className="flex justify-between gap-3 text-xs text-ink-muted">
        <span>{t(`imports.stages.${stage}`, { defaultValue: stage })}</span>
        <span className="tabular-nums">{Math.floor(ratio * 100)}%</span>
      </div>
      <ProgressBar value={ratio} label={t(`imports.stages.${stage}`, { defaultValue: stage })} />
    </div>
  );
}

function Summary({ job }: { job: Job }) {
  const { t } = useTranslation();
  const counts = job.result?.counts;
  if (job.kind === "rollback" && job.result)
    return (
      <span>
        {t("imports.jobs.rolledBack", {
          removed: formatNumber(job.result.tickets_removed ?? 0),
          restored: formatNumber(job.result.tickets_restored ?? 0),
        })}
      </span>
    );
  if (!counts) return null;
  return (
    <span>
      {t("imports.jobs.counts", {
        new: formatNumber(counts.tickets_new ?? 0),
        updated: formatNumber(counts.tickets_updated ?? 0),
        unchanged: formatNumber(counts.tickets_unchanged ?? 0),
      })}
    </span>
  );
}

function JobDialog({ jobId, onClose }: { jobId: number | null; onClose: () => void }) {
  const { t } = useTranslation();
  const job = useQuery({
    queryKey: ["imports", "job", jobId],
    queryFn: () => importsApi.job(jobId ?? 0),
    enabled: jobId !== null,
    refetchInterval: (query) => (query.state.data && jobIsActive(query.state.data) ? 2_000 : false),
  });
  const data = job.data;
  return (
    <Dialog
      open={jobId !== null}
      onOpenChange={(open) => !open && onClose()}
      title={t("imports.jobs.detailTitle", { id: jobId })}
      wide
    >
      {data && (
        <div className="space-y-4 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={TONE[data.status]}>{t(`imports.jobs.status.${data.status}`)}</Badge>
            {data.params.dry_run && <Badge tone="accent">{t("imports.jobs.dryRun")}</Badge>}
            <span className="text-ink-muted">
              {t(`imports.jobs.kinds.${data.kind}`)}
              {data.params.file ? ` · ${data.params.file}` : ""}
            </span>
          </div>
          <JobProgress job={data} />
          {data.status === "interrupted" && <Alert>{t("imports.jobs.interruptedHint")}</Alert>}
          {data.error && <Alert>{data.error}</Alert>}
          <Summary job={data} />
          {data.result?.warnings && data.result.warnings.length > 0 && (
            <Alert tone="accent">
              <ul className="list-disc space-y-1 pl-4">
                {data.result.warnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </Alert>
          )}
          {data.log && (
            <pre className="max-h-72 overflow-auto rounded-control bg-sunken p-3 text-xs leading-relaxed whitespace-pre-wrap">
              {data.log}
            </pre>
          )}
        </div>
      )}
    </Dialog>
  );
}

export function JobsCard() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const jobs = useQuery({
    queryKey: ["imports", "jobs"],
    queryFn: importsApi.jobs,
    refetchInterval: (query) => (query.state.data?.some(jobIsActive) ? 2_000 : 15_000),
  });
  const [details, setDetails] = useState<number | null>(null);
  const cancel = useMutation({
    mutationFn: importsApi.cancelJob,
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["imports"] }),
  });

  // When a job ends, what it changed (files in use, batches, the archive summary) is out of date.
  const wasActive = useRef(false);
  const active = jobs.data?.some(jobIsActive) ?? false;
  useEffect(() => {
    if (wasActive.current && !active) {
      void queryClient.invalidateQueries({ queryKey: ["imports"] });
      void queryClient.invalidateQueries({ queryKey: ["archive"] });
    }
    wasActive.current = active;
  }, [active, queryClient]);

  return (
    <Card aria-labelledby="jobs-title" className="overflow-hidden">
      <CardHeader title={<span id="jobs-title">{t("imports.jobs.title")}</span>} />
      <div className="relative overflow-x-auto">
        <table className="stack-table w-full min-w-[44rem] text-left text-sm">
          <thead className="border-b border-line bg-surface-2 text-xs text-ink-muted">
            <tr>
              <th scope="col" className="px-5 py-3 font-medium">
                {t("imports.jobs.job")}
              </th>
              <th scope="col" className="px-5 py-3 font-medium">
                {t("imports.jobs.state")}
              </th>
              <th scope="col" className="px-5 py-3 font-medium">
                {t("imports.jobs.outcome")}
              </th>
              <th scope="col" className="px-5 py-3 font-medium">
                {t("imports.jobs.when")}
              </th>
              <th scope="col" className="px-5 py-3 font-medium">
                <span className="sr-only">{t("imports.files.actions")}</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {jobs.data?.map((job) => (
              <tr key={job.id} className="align-top">
                <td className="px-5 py-3">
                  <div className="font-medium">
                    {t(`imports.jobs.kinds.${job.kind}`)} #{job.id}
                  </div>
                  <div className="text-xs break-all text-ink-subtle">
                    {job.params.file ?? ""}
                    {job.params.dry_run ? ` · ${t("imports.jobs.dryRun")}` : ""}
                  </div>
                </td>
                <td className="px-5 py-3" data-label={t("imports.jobs.state")}>
                  <div className="space-y-2">
                    <Badge tone={TONE[job.status]}>{t(`imports.jobs.status.${job.status}`)}</Badge>
                    <JobProgress job={job} />
                  </div>
                </td>
                <td className="px-5 py-3 text-ink-muted" data-label={t("imports.jobs.outcome")}>
                  {job.error ? <span className="text-danger">{job.error}</span> : <Summary job={job} />}
                </td>
                <td className="px-5 py-3 text-ink-muted tabular-nums" data-label={t("imports.jobs.when")}>
                  {formatDateTime(job.created_at)}
                  {elapsed(job) && (
                    <div className="text-xs">{t("imports.jobs.took", { time: elapsed(job) })}</div>
                  )}
                  {job.created_by && <div className="text-xs">{job.created_by}</div>}
                </td>
                <td className="px-5 py-3 text-right whitespace-nowrap max-sm:pt-2">
                  <div className="flex justify-end gap-2 max-sm:justify-start">
                    {job.status === "queued" && (
                      <Button variant="ghost" busy={cancel.isPending} onClick={() => cancel.mutate(job.id)}>
                        {t("imports.jobs.cancel")}
                      </Button>
                    )}
                    <Button
                      variant="secondary"
                      onClick={() => setDetails(job.id)}
                      aria-label={`${t("imports.jobs.details")} #${job.id}`}
                    >
                      {t("imports.jobs.details")}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {jobs.data?.length === 0 && (
          <p className="px-5 py-6 text-sm text-ink-muted">{t("imports.jobs.empty")}</p>
        )}
      </div>
      <JobDialog jobId={details} onClose={() => setDetails(null)} />
    </Card>
  );
}
